import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { Headset } from "lucide-react";
import {
  Search,
  CalendarDays,
  MessageCircle,
  ClipboardList,
  Settings,
  CheckCircle2,
  AlertTriangle,
  Edit3,
  HousePlus,
  Star,
} from "lucide-react";

interface ProfileItem {
  label: string;
  filled: boolean;
}

interface ProfileSidebarProps {
  userName: string;
  bio: string;
  avatarUrl?: string;
  profileItems: ProfileItem[];
}

const mainMenuItems = [
  { icon: Search, label: "Buscar Anfitriões", to: "/" },
  { icon: CalendarDays, label: "Minhas Reservas", to: "/tutor/dashboard?view=reservas" },
  { icon: Star, label: "Avaliações", to: "/tutor/dashboard?view=avaliacoes" },
  { icon: MessageCircle, label: "Mensagens", to: "/chat/inbox" },
  { icon: ClipboardList, label: "Prontuário", to: "/tutor/prontuarios" },
  { icon: Settings, label: "Meus Dados", to: "/tutor/editar" },
  { icon: Headset, label: "Tire suas dúvidas (IA)", to: "/painel/chat-suporte" },
];

const adoptionMenuItems = [
  { icon: ClipboardList, label: "Doar Pet", to: "/adocao/create" },
  { icon: ClipboardList, label: "Adotar Pet", to: "/adocao" },
];

export function ProfileSidebar({
  userName,
  bio,
  avatarUrl,
  profileItems,
}: ProfileSidebarProps) {
  const filled = profileItems.filter((i) => i.filled).length;
  const total = profileItems.length;
  const pct = total > 0 ? Math.round((filled / total) * 100) : 0;

  const authUser = (window as any).__AUTH_USER__;
  const hasOng = !!authUser?.has_ong;
  const ongPanelUrl = authUser?.ong_panel_url || "/adocao/minha-ong";

  return (
    <div className="space-y-4">
      <Card className="card-shadow border-0 overflow-hidden">
        <div className="gradient-primary h-20" />

        <CardContent className="relative pt-0 pb-5 px-5 -mt-10 text-center">
          <Avatar className="h-20 w-20 mx-auto border-4 border-card shadow-md">
            <AvatarImage src={avatarUrl} alt={userName} />
            <AvatarFallback className="bg-secondary text-secondary-foreground text-xl font-bold">
              {userName?.charAt(0) || "U"}
            </AvatarFallback>
          </Avatar>

          <h3 className="mt-3 text-lg font-bold text-foreground">{userName}</h3>
          <p className="text-sm text-muted-foreground">{bio}</p>

          <Button asChild variant="outline" size="sm" className="mt-3 gap-1.5">
            <a href="/tutor/editar">
              <Edit3 className="h-3.5 w-3.5" />
              Editar Perfil
            </a>
          </Button>

          <div className="mt-5 text-left">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-semibold text-foreground">
                Progresso: {pct}%
              </span>

              {pct < 100 && (
                <a
                  href="/tutor/editar"
                  className="text-xs font-bold text-coral hover:underline"
                >
                  Completar
                </a>
              )}
            </div>

            <Progress value={pct} className="h-2 bg-muted [&>div]:gradient-primary" />

            <div className="mt-3 flex flex-wrap gap-1.5">
              {profileItems.map((item) => (
                <span
                  key={item.label}
                  className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-md ${
                    item.filled
                      ? "bg-forest-light text-forest"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {item.filled ? (
                    <CheckCircle2 className="h-3 w-3" />
                  ) : (
                    <AlertTriangle className="h-3 w-3" />
                  )}
                  {item.label}
                </span>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="card-shadow border-0">
        <CardContent className="p-2">
          <nav className="space-y-0.5">
            {mainMenuItems.map((item) => (
              <div key={item.label}>
                <a
                  href={item.to}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground hover:bg-forest-light hover:text-forest transition-colors"
                >
                  <item.icon className="h-4 w-4 text-muted-foreground" />
                  {item.label}
                </a>

                {item.label === "Meus Dados" && (
                  <ChangePasswordDialog triggerClassName="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground hover:bg-forest-light hover:text-forest transition-colors text-left" />
                )}
              </div>
            ))}

            {hasOng && (
              <a
                href={ongPanelUrl}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground hover:bg-forest-light hover:text-forest transition-colors"
              >
                <ClipboardList className="h-4 w-4 text-muted-foreground" />
                Painel da ONG
              </a>
            )}
          </nav>
        </CardContent>
      </Card>

      <Card className="card-shadow border-0">
        <CardContent className="p-2">
          <p className="px-3 pb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
            Adoção
          </p>

          <nav className="space-y-0.5">
            {adoptionMenuItems.map((item) => (
              <a
                key={item.label}
                href={item.to}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground hover:bg-forest-light hover:text-forest transition-colors"
              >
                <item.icon className="h-4 w-4 text-muted-foreground" />
                {item.label}
              </a>
            ))}
          </nav>
        </CardContent>
      </Card>

      <Card className="card-shadow border-0">
        <CardContent className="p-2">
          <p className="px-3 pb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
            Anfitrião
          </p>

          <nav className="space-y-0.5">
            <a
              href="/solicitacao-anfitriao"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground hover:bg-forest-light hover:text-forest transition-colors"
            >
              <HousePlus className="h-4 w-4 text-muted-foreground" />
              Quero ser anfitrião
            </a>
          </nav>
        </CardContent>
      </Card>
    </div>
  );
}
